import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth, dashboardPathForRole, API } from '@/App';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const DEV_AUTH = process.env.REACT_APP_DEV_AUTH === '1';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState('client');
  const [warning, setWarning] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const navigate = useNavigate();
  const { user, setUser, config } = useAuth();
  const free = config?.free_mode;
  const roleRef = useRef(selectedRole);
  const googleBtnRef = useRef(null);

  // Keep the latest selected role available to the (once-bound) GIS callback.
  useEffect(() => { roleRef.current = selectedRole; }, [selectedRole]);

  useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam === 'astrologer' || roleParam === 'client') {
      setSelectedRole(roleParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      navigate(dashboardPathForRole(user.role), { replace: true });
    }
  }, [user, navigate]);

  const finishLogin = (userData) => {
    setUser(userData);
    navigate(dashboardPathForRole(userData.role), { replace: true });
  };

  // Render the Google Identity Services button once the GIS script is ready.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    const handleCredential = async (resp) => {
      setWarning('');
      setSigningIn(true);
      try {
        const r = await axios.post(
          `${API}/auth/google`,
          { credential: resp.credential, role: roleRef.current },
          { withCredentials: true }
        );
        finishLogin(r.data);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Google 登入失敗';
        setWarning(msg);
        toast.error(msg);
      } finally {
        setSigningIn(false);
      }
    };

    let timer;
    let attempts = 0;
    const tryInit = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id && googleBtnRef.current) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredential,
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          width: 320,
          text: 'signin_with',
          locale: 'zh_TW',
        });
      } else if (attempts++ < 25) {
        timer = setTimeout(tryInit, 200);
      } else {
        toast.error('Google 登入元件載入失敗，請檢查網絡或稍後重試');
      }
    };
    tryInit();
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const devLogin = async (role) => {
    try {
      const r = await axios.post(`${API}/auth/dev-login`, { role }, { withCredentials: true });
      finishLogin(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '開發登入失敗');
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-md w-full mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-3">
            歡迎來到命理配對平台
          </h2>
          <p className="text-base leading-relaxed text-[#4A4A4A] font-sans mb-8">
            使用 Google 帳號快速登入或註冊
          </p>

          <Tabs value={selectedRole} onValueChange={(v) => { setSelectedRole(v); setWarning(''); }} className="mb-8">
            <TabsList className="grid w-full grid-cols-2 rounded-sm">
              <TabsTrigger value="client" className="rounded-sm" data-testid="role-tab-client">
                求測者
              </TabsTrigger>
              <TabsTrigger value="astrologer" className="rounded-sm" data-testid="role-tab-astrologer">
                命理師
              </TabsTrigger>
            </TabsList>

            <TabsContent value="client" className="mt-6">
              <div className="bg-[#F5F0E6] border border-[#E5E0D8] rounded-sm p-6">
                <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">求測者帳號</h3>
                <p className="text-sm text-[#5C5C5C] font-sans">
                  提交求測單，讓多位命理師回答你的驗證問題，比較後選擇最適合的一位。
                </p>
                {free && (
                  <p className="text-sm text-[#2F7D4F] font-medium mt-2" data-testid="login-free-note">
                    推廣期免費 — 睇答案、揀命理師、開通諮詢都唔使錢。
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="astrologer" className="mt-6">
              <div className="bg-[#F5F0E6] border border-[#E5E0D8] rounded-sm p-6">
                <h3 className="font-serif text-lg text-[#1A1A1A] mb-2">命理師帳號</h3>
                <p className="text-sm text-[#5C5C5C] font-sans">
                  建立個人頁，瀏覽求測者求測單，回答驗證問題展現你的專業實力。
                </p>
              </div>
            </TabsContent>
          </Tabs>

          {warning && (
            <div className="mb-4 bg-[#FEF2F2] border border-[#C93B2B] rounded-sm p-3 text-sm text-[#C93B2B]" data-testid="login-warning">
              {warning}
            </div>
          )}

          {GOOGLE_CLIENT_ID ? (
            <>
              <div ref={googleBtnRef} className="flex justify-center" data-testid="google-login-btn" />
              {signingIn && (
                <p className="text-sm text-[#5C5C5C] text-center mt-3" data-testid="signing-in-note">
                  登入中…伺服器如啱啱由休眠中喚醒，可能需時約 1 分鐘，請稍候。
                </p>
              )}
            </>
          ) : (
            <div className="bg-[#F5F0E6] border border-[#E5E0D8] rounded-sm p-4 text-sm text-[#5C5C5C]">
              尚未設定 Google 登入（<code>REACT_APP_GOOGLE_CLIENT_ID</code>）。請參考 README 設定，或先用下方的開發登入。
            </div>
          )}

          {DEV_AUTH && (
            <div className="mt-6 border-t border-dashed border-[#E5E0D8] pt-4">
              <p className="text-xs text-[#9A9A9A] mb-2">開發快速登入（本地用，正式環境請關閉）</p>
              <div className="flex gap-2">
                <Button onClick={() => devLogin('client')} variant="outline" className="flex-1 rounded-sm" data-testid="dev-login-client">
                  求測者
                </Button>
                <Button onClick={() => devLogin('astrologer')} variant="outline" className="flex-1 rounded-sm" data-testid="dev-login-astrologer">
                  命理師
                </Button>
                <Button onClick={() => devLogin('admin')} variant="outline" className="flex-1 rounded-sm" data-testid="dev-login-admin">
                  管理員
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-[#5C5C5C] text-center mt-6">
            登入即表示你同意我們的
            <Link to="/terms" className="text-[#C93B2B] hover:underline">服務條款</Link>
            和
            <Link to="/privacy" className="text-[#C93B2B] hover:underline">隱私政策</Link>
          </p>
        </div>
      </div>

      <div
        className="hidden md:block bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/30918012/pexels-photo-30918012.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
        }}
      />
    </div>
  );
};

export default Login;
